/**
 * Note: customer facing cue values which should later be mapped
 * to the actual TTS cue ranges
 */
export const CUE_SLIDER_MAX = 100;
export const CUE_SLIDER_MIN = -100;
export const CUE_SLIDER_STEPS = 20;

export interface useSliderRangeProps {
  min?: number;
  max?: number;
  step?: number;
}

export const getNumberOfMarks = (min: number, max: number, step: number) => {
  const checkMin = Math.sign(min) ? min : Math.abs(min);
  const range = Math.abs(max) - checkMin;
  return Math.round(Math.abs(range / step)) - 1;
};

export const getIndexOfMiddleOfArray = (length: number) => {
  return Math.floor((length - 1) / 2);
};

const useSliderRange = ({
  min = CUE_SLIDER_MIN,
  max = CUE_SLIDER_MAX,
  step = CUE_SLIDER_STEPS,
}: useSliderRangeProps) => {
  const totalNumberOfMarks = getNumberOfMarks(min, max, step);
  const middleIndex = getIndexOfMiddleOfArray(totalNumberOfMarks);

  let count = min;
  const marks = Array(totalNumberOfMarks)
    .fill(0)
    .map(_ => {
      count += step;
      return {
        value: count,
        label: '',
      };
    });

  return {
    marks,
    noCueValue: marks[middleIndex].value,
    min,
    max,
    step,
  };
};

/**
 * We need the starting point of the slider to be the mid point and the track to extend
 * left or right based on the value of the slider.
 *
 * For example, if the value is -50, the track should start at 0 and extend to the left
 * by 25% of the slider width.
 *
 * If the value is 50, the track should start at 50% of the slider width and extend to the right.
 */
export const getSliderTrackStyles = (
  value: number
): { left: string; width: string } => {
  let width;
  let left;
  if (value <= 0) {
    // left starts at 50 and goes to 0 as value goes from 0 to -100
    // width starts at 0 and goes to 50 as value goes from 0 to -100
    left = 50 + value / 2; // Scales from 50 to 0 as value goes from 0 to -100
    width = -value / 2; // Scales from 0 to 50 as value goes from 0 to -100
  } else {
    // For values greater than 0
    left = 50;
    width = value / 2; // Scales from 0 to 50 as value goes from 0 to 100
  }

  return {
    left: `${Math.round(left)}% !important`,
    width: `${Math.round(width)}% !important`,
  };
};

export default useSliderRange;
